import { useEffect, useState } from "react";
import { Heart, Plus, Power, Crown, Mail, Phone, X, Flag, Trash2, Check, ImageOff } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Admin() {
  const { user } = useAuth();
  const [codes, setCodes] = useState([]);
  const [avis, setAvis] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [members, setMembers] = useState([]);
  const [reports, setReports] = useState([]);
  const AGE_LABELS = { "30-40": "30/40 ans", "40-50": "40/50 ans", "30-55": "30/55 ans", "50-plus": "50 ans et +" };
  const [newCode, setNewCode] = useState("");
  const [maxUses, setMaxUses] = useState(10);
  const [newKind, setNewKind] = useState("test");
  const [replies, setReplies] = useState({});

  const isAdmin = user && user.role === "admin";

  const load = () => {
    if (!isAdmin) return;
    api.get("/admin/promo-codes").then((r) => setCodes(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/avis").then((r) => setAvis(r.data)).catch(() => {});
    api.get("/admin/speed-reservations").then((r) => setReservations(r.data)).catch(() => {});
    api.get("/admin/members").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {});
  };

  const resolveReport = async (id) => {
    try {
      await api.post(`/admin/reports/${id}/resolve`);
      setReports((rs) => rs.map((r) => (r.id === id ? { ...r, status: "traite" } : r)));
      toast.success("Signalement marqué comme traité");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteMember = async (id, name) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${name} ?\n\nSes données personnelles seront anonymisées (nom, email, photo), le compte ne pourra plus se connecter ni apparaître, et les signalements le concernant seront clos. Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/members/${id}`);
      setMembers((ms) => ms.filter((m) => m.id !== id));
      toast.success("Compte supprimé et anonymisé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removePhoto = async (id, name) => {
    if (!window.confirm(`Retirer la photo de profil de ${name} ?\n\nLe profil restera actif mais n'apparaîtra plus dans les rencontres tant qu'une nouvelle photo validée n'aura pas été ajoutée.`)) return;
    try {
      await api.post(`/admin/members/${id}/remove-photo`);
      setMembers((ms) => ms.map((m) => (m.id === id ? { ...m, avatar: null } : m)));
      toast.success("Photo retirée — le membre devra en ajouter une nouvelle");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleMember = async (id) => {
    try {
      const { data } = await api.post(`/admin/members/${id}/toggle-active`);
      setMembers((ms) => ms.map((m) => (m.id === id ? { ...m, active: data.active } : m)));
      toast.success(data.active ? "Profil réactivé" : "Profil suspendu — il n'apparaît plus dans les rencontres");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteReservation = async (id) => {
    try {
      await api.delete(`/admin/speed-reservations/${id}`);
      setReservations((rs) => rs.filter((r) => r.id !== id));
      toast.success("Réservation supprimée");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const createCode = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/promo-codes", { code: newCode, max_uses: Number(maxUses), kind: newKind });
      toast.success(`Code ${newCode.toUpperCase()} créé (${newKind === "speed" ? "Speed Dating" : "test de compatibilité"})`);
      setNewCode("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggle = async (code) => {
    await api.post(`/admin/promo-codes/${code}/toggle`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const sendReply = async (userId) => {
    const reply = (replies[userId] || "").trim();
    if (!reply) return;
    try {
      await api.post(`/admin/avis/${userId}/reply`, { reply });
      toast.success("Réponse publiée");
      setReplies((r) => ({ ...r, [userId]: "" }));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center" data-testid="admin-denied">
        <Crown size={32} className="mx-auto text-gold" />
        <h1 className="mt-4 font-serif text-2xl font-medium text-ink">Cet espace est réservé à Alexandra</h1>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-8" data-testid="admin-page">
      <p className="font-cinzel text-xs font-semibold tracking-[0.35em] text-gold">ESPACE FONDATRICE</p>
      <h1 className="mt-3 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">Bonjour Alexandra</h1>

      {/* Codes promo */}
      <section className="mt-10 rounded-3xl border border-terra/15 bg-white p-7 shadow-boho" data-testid="admin-promo-section">
        <h2 className="font-serif text-2xl font-medium text-ink">Codes d'invitation</h2>
        <p className="mt-1 text-sm text-clay">Un code « Test » offre le test de compatibilité (10 €) ; un code « Speed Dating » offre une soirée (34 €).</p>
        <form onSubmit={createCode} className="mt-5 flex flex-wrap items-end gap-3">
          <div className="min-w-40 flex-1">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">Code</label>
            <input
              value={newCode}
              onChange={(e) => setNewCode(e.target.value.toUpperCase())}
              data-testid="promo-code-input"
              placeholder="EX : MAMAN-SOLO"
              required
              className="w-full rounded-xl border border-terra/20 bg-cream px-4 py-3 text-sm uppercase outline-none focus:border-terra"
            />
          </div>
          <div className="w-44">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">Offre</label>
            <select
              value={newKind}
              onChange={(e) => setNewKind(e.target.value)}
              data-testid="promo-kind-select"
              className="w-full rounded-xl border border-terra/20 bg-cream px-4 py-3 text-sm outline-none focus:border-terra"
            >
              <option value="test">Test de compatibilité</option>
              <option value="speed">Speed Dating</option>
            </select>
          </div>
          <div className="w-32">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-terra">Utilisations</label>
            <input
              type="number"
              min={1}
              max={1000}
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              data-testid="promo-max-input"
              className="w-full rounded-xl border border-terra/20 bg-cream px-4 py-3 text-sm outline-none focus:border-terra"
            />
          </div>
          <button type="submit" data-testid="promo-create-button" className="flex items-center gap-2 rounded-full bg-terra px-5 py-3 text-sm font-semibold text-cream hover:bg-terra-dark">
            <Plus size={15} /> Créer
          </button>
        </form>
        <div className="mt-6 space-y-2" data-testid="promo-list">
          {codes.map((c) => (
            <div key={c.code} className="flex items-center justify-between rounded-xl border border-terra/10 bg-cream px-4 py-3" data-testid={`promo-item-${c.code}`}>
              <div>
                <span className="font-cinzel text-sm font-semibold tracking-[0.15em] text-ink">{c.code}</span>
                <span className="ml-3 rounded-full bg-terra/10 px-2 py-0.5 text-[10px] font-semibold text-terra">{c.kind === "speed" ? "Speed Dating" : "Test"}</span>
                <span className="ml-2 text-xs text-stone2">{c.uses || 0}/{c.max_uses ? c.max_uses : "∞"} utilisations</span>
              </div>
              <button
                onClick={() => toggle(c.code)}
                data-testid={`promo-toggle-${c.code}`}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${c.active ? "bg-gold-light text-ink" : "bg-stone2/20 text-stone2"}`}
              >
                <Power size={12} /> {c.active ? "Actif" : "Inactif"}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Membres — vérification anti faux profils */}
      <section className="mt-8 rounded-3xl border border-terra/15 bg-white p-7 shadow-boho" data-testid="admin-members-section">
        <h2 className="font-serif text-2xl font-medium text-ink">Membres</h2>
        <p className="mt-1 text-sm text-clay">Vérifiez les profils : photo obligatoire pour apparaître dans les rencontres, et vous pouvez suspendre un profil douteux.</p>
        <div className="mt-6 space-y-3">
          {members.length === 0 && <p className="text-sm italic text-stone2">Aucun membre pour le moment.</p>}
          {members.map((m) => (
            <div key={m.id} className={`flex items-center gap-4 rounded-xl border px-4 py-3 ${m.active ? "border-terra/10 bg-cream" : "border-red-200 bg-red-50"}`} data-testid={`admin-member-${m.id}`}>
              {m.avatar ? (
                <img src={m.avatar} alt={m.name} className="h-12 w-12 shrink-0 rounded-full border border-terra/20 object-cover" />
              ) : (
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-dashed border-terra/40 bg-white text-[9px] font-semibold uppercase text-terra">Sans photo</span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-serif text-base font-semibold text-ink">
                  {m.name}{m.age ? `, ${m.age}` : ""} {m.city ? <span className="text-xs font-normal text-stone2">· {m.city}</span> : null}
                  {m.created_at && (Date.now() - new Date(m.created_at).getTime()) < 7 * 24 * 3600 * 1000 && (
                    <span className="ml-2 rounded-full bg-gold/25 px-2 py-0.5 align-middle text-[9px] font-bold uppercase tracking-wider text-[#8A6D1F]" data-testid={`admin-member-new-${m.id}`}>Nouveau</span>
                  )}
                </p>
                <p className="truncate text-xs text-clay">{m.email}</p>
                <p className="mt-0.5 text-[11px] text-stone2">
                  {m.quiz_done ? m.archetype_label || "Test fait" : "Test pas encore fait"} · {m.paid ? "Payé" : "Non payé"} · âge déclaré{m.age_verified ? ", vérifié" : ", non vérifié"}
                </p>
              </div>
              <button
                onClick={() => toggleMember(m.id)}
                data-testid={`admin-member-toggle-${m.id}`}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                  m.active ? "bg-terra/10 text-terra hover:bg-red-100 hover:text-red-700" : "bg-red-600 text-white hover:bg-red-700"
                }`}
              >
                {m.active ? "Suspendre" : "Réactiver"}
              </button>
              {m.avatar && (
                <button
                  onClick={() => removePhoto(m.id, m.name)}
                  data-testid={`admin-member-remove-photo-${m.id}`}
                  title="Retirer la photo de profil (contenu inapproprié)"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-terra/25 text-terra transition-colors hover:bg-terra hover:text-white"
                >
                  <ImageOff size={13} />
                </button>
              )}
              <button
                onClick={() => deleteMember(m.id, m.name)}
                data-testid={`admin-member-delete-${m.id}`}
                title="Supprimer définitivement ce compte (données anonymisées)"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-red-200 text-red-500 transition-colors hover:bg-red-600 hover:text-white"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Signalements — registre de modération */}
      <section className="mt-8 rounded-3xl border border-terra/15 bg-white p-7 shadow-boho" data-testid="admin-reports-section">
        <h2 className="flex items-center gap-2 font-serif text-2xl font-medium text-ink"><Flag size={20} className="text-terra" /> Signalements</h2>
        <p className="mt-1 text-sm text-clay">Registre de modération : chaque signalement envoyé par un membre arrive ici (et par email). Gardez-le à jour — il sert de justificatif de modération.</p>
        <div className="mt-6 space-y-3">
          {reports.length === 0 && <p className="text-sm italic text-stone2">Aucun signalement — la communauté se porte bien.</p>}
          {reports.map((r) => (
            <div key={r.id} className={`rounded-xl border p-4 ${r.status === "nouveau" ? "border-red-200 bg-red-50/60" : "border-terra/10 bg-cream"}`} data-testid={`admin-report-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-serif text-sm font-semibold text-ink">
                  {r.target_name} <span className="text-xs font-normal text-stone2">signalé·e par {r.reporter_name}</span>
                </p>
                <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${r.status === "nouveau" ? "bg-red-600 text-white" : "bg-[#3E5641] text-cream"}`} data-testid={`admin-report-status-${r.id}`}>
                  {r.status === "nouveau" ? "À traiter" : "Traité"}
                </span>
              </div>
              <p className="mt-1 text-xs font-semibold text-terra">{r.reason_label} <span className="font-normal text-stone2">· {r.context === "conversation" ? "dans une conversation" : "sur le profil"}</span></p>
              {r.detail && <p className="mt-1 text-sm text-clay">« {r.detail} »</p>}
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-stone2">
                <a href={`mailto:${r.target_email}`} className="font-semibold text-terra hover:text-terra-dark">{r.target_email}</a>
                <span>{new Date(r.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
              </div>
              {r.status === "nouveau" && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => resolveReport(r.id)} data-testid={`admin-report-resolve-${r.id}`} className="flex items-center gap-1.5 rounded-full bg-[#3E5641] px-3.5 py-1.5 text-xs font-semibold text-cream hover:opacity-90">
                    <Check size={12} /> Marquer traité
                  </button>
                  {r.target_id && (
                    <button onClick={() => toggleMember(r.target_id)} data-testid={`admin-report-suspend-${r.id}`} className="flex items-center gap-1.5 rounded-full bg-terra/10 px-3.5 py-1.5 text-xs font-semibold text-terra hover:bg-red-100 hover:text-red-700">
                      <Power size={12} /> Suspendre / réactiver le compte
                    </button>
                  )}
                  {r.target_id && (
                    <button onClick={() => removePhoto(r.target_id, r.target_name)} data-testid={`admin-report-remove-photo-${r.id}`} className="flex items-center gap-1.5 rounded-full bg-terra/10 px-3.5 py-1.5 text-xs font-semibold text-terra hover:bg-red-100 hover:text-red-700">
                      <ImageOff size={12} /> Retirer la photo
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Réponses aux avis */}
      <section className="mt-8 rounded-3xl border border-terra/15 bg-white p-7 shadow-boho" data-testid="admin-avis-section">
        <h2 className="font-serif text-2xl font-medium text-ink">Répondre aux avis</h2>
        <p className="mt-1 text-sm text-clay">Vos réponses s'affichent publiquement sous chaque avis.</p>
        <div className="mt-6 space-y-5">
          {avis.length === 0 && <p className="text-sm italic text-stone2">Aucun avis pour le moment.</p>}
          {avis.map((a, i) => (
            <div key={a.user_id} className="rounded-xl border border-terra/10 bg-cream p-5" data-testid={`admin-avis-${i}`}>
              <div className="flex items-center gap-2">
                <span className="font-serif text-base font-semibold text-ink">{a.name}</span>
                <span className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Heart key={n} size={11} className={n <= a.rating ? "text-terra" : "text-stone2/30"} fill={n <= a.rating ? "#C86D51" : "none"} />
                  ))}
                </span>
              </div>
              <p className="mt-2 text-sm text-clay">{a.text}</p>
              {a.reply && (
                <p className="mt-2 border-l-2 border-gold pl-3 text-xs italic text-ink">Votre réponse : {a.reply}</p>
              )}
              <div className="mt-3 flex gap-2">
                <input
                  value={replies[a.user_id] || ""}
                  onChange={(e) => setReplies((r) => ({ ...r, [a.user_id]: e.target.value }))}
                  data-testid={`admin-reply-input-${i}`}
                  placeholder={a.reply ? "Modifier votre réponse…" : "Votre réponse…"}
                  className="flex-1 rounded-xl border border-terra/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-terra"
                />
                <button onClick={() => sendReply(a.user_id)} data-testid={`admin-reply-button-${i}`} className="rounded-xl bg-plum px-4 py-2.5 text-sm font-semibold text-cream hover:bg-ink">
                  Publier
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Réservations Speed Dating */}
      <section className="mt-8 rounded-3xl border border-terra/15 bg-white p-7 shadow-boho" data-testid="admin-speed-section">
        <h2 className="font-serif text-2xl font-medium text-ink">Réservations Speed Dating</h2>
        <p className="mt-1 text-sm text-clay">Chaque demande vous arrive aussi par email — prévenez-les dès qu'une date est fixée.</p>
        <div className="mt-6 space-y-3">
          {reservations.length === 0 && <p className="text-sm italic text-stone2">Aucune réservation pour le moment.</p>}
          {reservations.map((r, i) => (
            <div key={r.id || i} className="rounded-xl border border-terra/10 bg-cream p-4" data-testid={`speed-reservation-${i}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-serif text-sm font-semibold text-ink">{r.name}</p>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${r.payment_status === "paid" ? "bg-[#3E5641] text-cream" : "bg-stone2/20 text-stone2"}`} data-testid={`speed-reservation-paid-${i}`}>
                    {r.payment_status === "paid" ? "Payée" : "Paiement en attente"}
                  </span>
                  <span className="rounded-full bg-[#5C1E28] px-3 py-1 text-[11px] font-semibold text-cream" data-testid={`speed-reservation-group-${i}`}>
                    {AGE_LABELS[r.age_group] || r.age_group}
                  </span>
                  <button
                    onClick={() => deleteReservation(r.id)}
                    data-testid={`speed-reservation-delete-${i}`}
                    aria-label="Supprimer"
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-terra/20 text-terra transition-colors hover:bg-terra hover:text-cream"
                  >
                    <X size={13} />
                  </button>
                </div>
              </div>
              <a href={`mailto:${r.email}`} className="mt-1 flex w-fit items-center gap-1.5 text-xs font-semibold text-terra hover:text-terra-dark">
                <Mail size={12} /> {r.email}
              </a>
              {r.phone && (
                <a href={`tel:${r.phone}`} className="mt-1 flex w-fit items-center gap-1.5 text-xs font-semibold text-terra hover:text-terra-dark" data-testid={`speed-reservation-phone-${i}`}>
                  <Phone size={12} /> {r.phone}
                </a>
              )}
              {r.message && <p className="mt-1 text-sm text-clay">{r.message}</p>}
              <p className="mt-1 text-[11px] text-stone2">{new Date(r.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
